<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-6">
					<h3 class="page-header">
						<i class="fa fa-calendar-check-o animated bounceInDown show-info"></i>
						Daily Attendance
					</h3>
				</div>
			</div>
		</div>

		<!-- Controls -->
		<div class="col-md-12">
			<div class="row stock_section">
				<div class="col-md-3">
					<label>Date</label>
					<input
						type="date"
						class="form-control form-cascade-control input-box-short"
						v-model="isoDate"
						:max="today"
						@input="onDateInput"
					/>
				</div>
				<div class="col-md-3">
					<label>Search</label>
					<input
						type="text"
						class="form-control form-cascade-control input-box-short"
						placeholder="Search name or code"
						v-model="search"
					/>
				</div>
				<div class="col-md-3">
					<label class="d-block">&nbsp;</label>
					<label class="checkbox-inline">
						<input type="checkbox" v-model="onlyExceptions" @change="refresh" />
						Only rows needing attention
					</label>
				</div>
				<div class="col-md-3 text-right">
					<label class="d-block">&nbsp;</label>
					<span v-if="register && register.is_locked" class="badge bg-secondary" style="margin-right:6px">
						<i class="fa fa-lock"></i> Period locked
					</span>
					<button
						class="btn btn-primary"
						:disabled="recomputing || (register && register.is_locked)"
						@click="recomputeDay"
					>
						<i class="fa fa-refresh" :class="{ 'fa-spin': recomputing }"></i> Recompute day
					</button>
				</div>
			</div>
		</div>

		<!-- Totals -->
		<div class="col-md-12" v-if="totals" style="margin-top:10px">
			<span class="badge bg-success totals-badge">Present {{ totals.present }}</span>
			<span class="badge bg-warning text-dark totals-badge">Half day {{ totals.half_day }}</span>
			<span class="badge bg-danger totals-badge">Absent {{ totals.absent }}</span>
			<span class="badge bg-info text-dark totals-badge">Leave {{ totals.on_leave }}</span>
			<span class="badge bg-secondary totals-badge">Weekly off {{ totals.weekly_off }}</span>
			<span class="badge bg-secondary totals-badge">Holiday {{ totals.holiday }}</span>
			<span class="badge bg-warning text-dark totals-badge">Late {{ totals.late }}</span>
			<span class="badge bg-danger totals-badge">Exceptions {{ totals.exceptions }}</span>
			<span class="badge bg-dark totals-badge">Payable days {{ totals.payable_days }}</span>
		</div>

		<div class="col-md-12" v-if="error" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<div class="col-md-12 table-responsive" style="padding:0px;margin-top:10px" v-else>
			<div class="panel">
				<div class="panel-body table" style="overflow-x:auto;padding-left:0px">
					<table class="table table-bordered table-hover table-striped display">
						<thead class="table-font">
							<th>Employee</th>
							<th>Status</th>
							<th>In</th>
							<th>Out</th>
							<th>Worked</th>
						<th>App access</th>
						<th></th>
						</thead>
						<tbody>
							<tr v-if="loading">
								<td colspan="7" class="text-center">Loading…</td>
							</tr>
							<tr v-else-if="!rows.length">
								<td colspan="7" class="text-center">No employees match this view.</td>
							</tr>
							<tr v-for="row in rows" :key="row.employee_id">
								<td>
									<a href="javascript:void(0)" class="text-primary" @click="openEmployee(row)">
										<strong>{{ row.name || '—' }}</strong>
									</a>
									<br />
									<small class="text-muted">
										{{ row.employee_code || '' }}{{ row.policy ? ' · ' + row.policy : '' }}
									</small>
								</td>
								<td>
									<span :class="statusBadgeClass(row.day.status)">{{ statusLabel(row.day.status) }}</span>
								</td>
								<td>
									<span :class="{ 'text-warning font-weight-bold': row.day.is_late }">
										{{ row.day.first_in || '—' }}
									</span>
								</td>
								<td>{{ row.day.last_out || '—' }}</td>
								<td>{{ minutesToHours(row.day.effective_minutes) }}</td>
								<td style="white-space:nowrap">
									<span v-if="row.app_access && !row.app_access.allowed" class="text-danger">
										<strong>Locked</strong><br />{{ row.app_access.reason === 'missing_out' ? 'Missing out' : 'Missing in' }}
										<br /><small>{{ row.app_access.work_date }}</small>
									</span>
									<span v-else class="text-muted">—</span>
								</td>
								<td style="white-space:nowrap">
									<button
										v-if="row.app_access && !row.app_access.allowed"
										class="btn btn-sm btn-warning daily-action-btn"
										title="Correct the actual missing punch; this changes attendance and payroll"
										@click="openCorrection(row)"
									>
										<i class="fa fa-pencil"></i><span class="daily-action-label">Correct</span>
									</button>
									<button
										class="btn btn-sm btn-primary daily-action-btn"
										title="Add a punch"
										:disabled="row.day.is_locked"
										@click="openPunchDialog(row)"
									>
										<i class="fa fa-plus-circle"></i><span class="daily-action-label">Punch</span>
									</button>
									<button
										class="btn btn-sm btn-danger daily-action-btn"
										title="Add a deduction for this day"
										:disabled="row.day.is_locked"
										@click="openDeductionDialog(row)"
									>
										<i class="fa fa-inr"></i><span class="daily-action-label">Deduction</span>
									</button>
									<button
										class="btn btn-sm btn-success daily-action-btn"
										title="Add or edit performance for this day"
										@click="openPerformanceDialog(row)"
									>
										<i class="fa fa-line-chart"></i><span class="daily-action-label">Performance</span>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!-- A deduction against this person on the date being viewed. -->
		<modal name="attendance-daily-deduction" :width="640" :height="'auto'" :adaptive="true">
			<div style="padding:18px">
				<h4 class="modal-header-title">Add deduction</h4>
				<deduction-form
					ref="deductionForm"
					:employee-id="deductionTarget ? deductionTarget.employee_id : ''"
					:employee-name="deductionTarget ? deductionTarget.name : ''"
					:work-date="isoDate"
					@saved="onDeductionSaved"
					@cancel="$modal.hide('attendance-daily-deduction')"
				/>
			</div>
		</modal>

		<modal name="attendance-daily-performance" :width="620" :height="'auto'" :adaptive="true">
			<div style="padding:18px">
				<h4 class="modal-header-title">Performance</h4>
				<performance-form
					ref="performanceForm"
					:employee-id="performanceTarget ? performanceTarget.employee_id : ''"
					:employee-name="performanceTarget ? performanceTarget.name : ''"
					:work-date="isoDate"
					:performance="selectedPerformance"
					@saved="onPerformanceSaved"
					@cancel="$modal.hide('attendance-daily-performance')"
				/>
			</div>
		</modal>

		<!-- Manual punch -->
		<modal name="attendance-punch" :width="440" :height="'auto'" :adaptive="true">
			<div class="p-3" style="padding:18px">
				<h4 class="modal-header-title">Add a punch</h4>
				<p class="text-muted" v-if="punchTarget">
					For {{ punchTarget.name }} on {{ punchWorkDate }}. Record the actual time: this changes
					attendance and payroll and is kept in the punch audit trail.
				</p>
				<div class="form-group">
					<label>Direction</label>
					<select class="form-control form-cascade-control" v-model="punchDirection">
						<option value="in">Punch in</option>
						<option value="out">Punch out</option>
					</select>
				</div>
				<div class="form-group">
					<label>Time</label>
					<input type="time" class="form-control form-cascade-control" v-model="punchTime" />
				</div>
				<div class="form-group">
					<label>Note</label>
					<input
						type="text"
						class="form-control form-cascade-control"
						placeholder="Why this was entered by hand"
						v-model="punchNote"
					/>
				</div>
				<div class="text-right" style="margin-top:12px">
					<button class="btn btn-default" @click="$modal.hide('attendance-punch')">Cancel</button>
					<button class="btn btn-primary" :disabled="savingPunch" @click="saveManualPunch()">
						Save punch
					</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import debounce from 'lodash.debounce'
import { registerService, performanceService, errorMessage } from './attendanceService'
import DeductionForm from './DeductionForm.vue'
import PerformanceForm from './PerformanceForm.vue'
import attendanceFormat from './attendanceFormat'

export default {
	name: 'AttendanceDaily',
	components: { DeductionForm, PerformanceForm },
	mixins: [attendanceFormat],
	data() {
		return {
			isoDate: this.toIsoDate(
				this.$route.query.date ? new Date(String(this.$route.query.date)) : new Date()
			),
			today: this.toIsoDate(new Date()),
			onlyExceptions: this.$route.query.exceptions === '1',
			search: '',
			register: null,
			loading: false,
			error: '',
			recomputing: false,
			deductionTarget: null,
			performanceTarget: null,
			performanceRows: [],
			// Manual punch
			punchTarget: null,
			punchWorkDate: '',
			punchDirection: 'in',
			punchTime: '09:00',
			punchNote: '',
			savingPunch: false,
		}
	},
	created() {
		this.debouncedDateRefresh = debounce(this.refresh, 1000)
	},
	computed: {
		rows() {
			const all = (this.register && this.register.rows) || []
			const term = this.search.trim().toLowerCase()
			if (!term) return all
			return all.filter(
				(r) =>
					(r.name && r.name.toLowerCase().includes(term)) ||
					(r.employee_code && r.employee_code.toLowerCase().includes(term))
			)
		},
		totals() {
			return this.register && this.register.totals
		},
		selectedPerformance() {
			if (!this.performanceTarget) return null
			return this.performanceRows.find(
				(row) => String(row.employee_id) === String(this.performanceTarget.employee_id)
			) || null
		},
	},
	mounted() {
		this.refresh()
	},
	beforeDestroy() {
		this.debouncedDateRefresh.cancel()
	},
	methods: {
		onDateInput() {
			this.debouncedDateRefresh()
		},
		refresh() {
			this.loading = true
			this.error = ''
			Promise.all([
				registerService.daily({
					date: this.isoDate,
					only_exceptions: this.onlyExceptions || undefined,
				}),
				performanceService.list(this.isoDate),
			])
				.then(([register, performance]) => {
					this.register = register
					this.performanceRows = (performance && performance.rows) || []
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load the register.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		recomputeDay() {
			this.recomputing = true
			registerService
				.recompute({ from_date: this.isoDate, to_date: this.isoDate })
				.then((result) => {
					this.$notify({
						text: `Recomputed: ${result.days} employee-day(s) rebuilt from punches.`,
						type: 'success',
					})
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Recompute failed'), type: 'error' })
				})
				.finally(() => {
					this.recomputing = false
				})
		},
		openPunchDialog(row) {
			this.punchTarget = row
			this.punchWorkDate = this.isoDate
			this.punchDirection = row.day.first_in ? 'out' : 'in'
			this.punchTime = '09:00'
			this.punchNote = ''
			this.$modal.show('attendance-punch')
		},
		openCorrection(row) {
			this.punchTarget = row
			this.punchWorkDate = row.app_access.work_date
			this.punchDirection = row.app_access.reason === 'missing_out' ? 'out' : 'in'
			this.punchTime = ''
			this.punchNote = ''
			this.$modal.show('attendance-punch')
		},
		openDeductionDialog(row) {
			this.deductionTarget = row
			this.$modal.show('attendance-daily-deduction')
			// The form is reused across rows, so clear the last entry.
			this.$nextTick(() => {
				if (this.$refs.deductionForm) this.$refs.deductionForm.reset()
			})
		},
		onDeductionSaved() {
			this.$notify({ text: 'Deduction added.', type: 'success' })
			this.$modal.hide('attendance-daily-deduction')
		},
		openPerformanceDialog(row) {
			this.performanceTarget = row
			this.$modal.show('attendance-daily-performance')
			this.$nextTick(() => {
				if (this.$refs.performanceForm) this.$refs.performanceForm.reset(this.selectedPerformance)
			})
		},
		onPerformanceSaved(row) {
			const index = this.performanceRows.findIndex(
				(item) => String(item.employee_id) === String(row.employee_id)
			)
			if (index >= 0) this.$set(this.performanceRows, index, row)
			else this.performanceRows.push(row)
			this.$notify({ text: 'Performance saved.', type: 'success' })
			this.$modal.hide('attendance-daily-performance')
		},
		/**
		 * A day inside a payroll lock is refused with `period_locked`; the admin
		 * is asked, and a yes resends the same punch with override_lock.
		 */
		saveManualPunch(overrideLock = false) {
			if (!this.punchTarget) return
			if (!this.punchTime) {
				this.$notify({ text: 'Enter the actual punch time.', type: 'error' })
				return
			}
			this.savingPunch = true
			// Send local wall-clock time; the server records both this and its own
			// receipt time, so an admin entry is auditable like any other punch.
			const stamp = `${this.punchWorkDate} ${this.punchTime}:00`
			registerService
				.manualPunch({
					employee_id: this.punchTarget.employee_id,
					direction: this.punchDirection,
					punched_at: stamp,
					note: this.punchNote || undefined,
					override_lock: overrideLock || undefined,
				})
				.then(() => {
					this.savingPunch = false
					this.$notify({
						text: overrideLock ? 'Punch recorded in a locked period' : 'Punch recorded',
						type: overrideLock ? 'warn' : 'success',
					})
					this.$modal.hide('attendance-punch')
					this.refresh()
				})
				.catch((e) => {
					this.savingPunch = false
					const errors = (e && e.response && e.response.data && e.response.data.errors) || {}
					if (errors.period_locked && !overrideLock) {
						if (window.confirm(`${errors.period_locked[0]}\n\nChange it anyway?`)) {
							this.saveManualPunch(true)
						}
						return
					}
					this.$notify({
						text: errorMessage(e, 'Could not record the punch'),
						type: 'error',
					})
				})
		},
		openEmployee(row) {
			this.$router.push({
				name: 'attendance-employee-month',
				params: { employeeId: row.employee_id },
				query: { month: this.isoDate.slice(0, 7) },
			})
		},
	},
}
</script>

<style scoped>
.totals-badge {
	margin-right: 6px;
	margin-bottom: 6px;
	display: inline-block;
	font-size: 12px;
}
.modal-header-title {
	margin-top: 0;
}
/* Wide enough for the word under the icon, which says what the icon means. */
.daily-action-btn {
	width: 62px;
	height: auto;
	padding: 4px 2px;
	text-align: center;
	vertical-align: top;
}
.daily-action-label {
	display: block;
	font-size: 9px;
	line-height: 1.2;
	margin-top: 2px;
	letter-spacing: 0.02em;
}
</style>
